/* Zeepy campaign landing page — countdown + sticky bar behaviour */
(function () {
  'use strict';

  function pad(n) { return String(n).padStart(2, '0'); }

  class ZcCountdown extends HTMLElement {
    connectedCallback() {
      var raw = (this.dataset.deadline || '').trim();
      this.deadline = raw ? new Date(raw.replace(' ', 'T')).getTime() : NaN;
      if (isNaN(this.deadline)) { this.hidden = true; return; }
      this.tick();
      this.timer = setInterval(this.tick.bind(this), 1000);
    }

    disconnectedCallback() { clearInterval(this.timer); }

    tick() {
      var left = Math.max(0, Math.floor((this.deadline - Date.now()) / 1000));
      var d = Math.floor(left / 86400);
      var h = Math.floor((left % 86400) / 3600);
      var m = Math.floor((left % 3600) / 60);
      var s = left % 60;

      this.set('days', d);
      this.set('hours', h);
      this.set('mins', m);
      this.set('secs', s);

      var short = this.querySelector('[data-zc-short]');
      if (short) short.textContent = d > 0 ? d + 'd ' + h + 'h' : h + 'h ' + pad(m) + 'm';

      if (left === 0) {
        clearInterval(this.timer);
        if (this.dataset.hideWhenDone === 'true') this.hidden = true;
      }
    }

    set(key, value) {
      var el = this.querySelector('[data-zc-' + key + ']');
      if (el) el.textContent = pad(value);
    }
  }

  if (!customElements.get('zc-countdown')) {
    customElements.define('zc-countdown', ZcCountdown);
  }

  /* Count-up — a stat figure animates from zero the first time it is seen.
     The finished number is SERVER-RENDERED inside this element, so Google, a
     no-JS visitor and anyone on reduced motion all get the real figure with
     no scripting. This only ever counts toward text already in the DOM, and
     puts that exact string back at the end — it never invents content, which
     keeps it on the right side of the "Liquid renders, JS decorates" rule. */
  class ZcCountUp extends HTMLElement {
    connectedCallback() {
      if (this.dataset.zcReady === 'true') return;
      this.dataset.zcReady = 'true';

      this.finalText = (this.textContent || '').trim();

      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced || !('IntersectionObserver' in window)) return;

      // Optional symbols, ONE number, optional symbols. A figure with a second
      // number in it — "2–6 yrs" — fails this deliberately and is left alone,
      // as is anything with no digits at all, like "No blue light".
      var parts = this.finalText.match(/^(\D*)(\d[\d,]*(?:\.\d+)?)(\D*)$/);
      if (!parts) return;

      // zcPrefix, not prefix: Element.prototype.prefix is a real DOM property
      // (the XML namespace prefix) and it is GETTER-ONLY. Assigning to it
      // throws under 'use strict', and because this runs inside a custom
      // element callback the throw is swallowed into a console error — the
      // element simply stops setting itself up, with nothing visibly broken.
      // Cost an hour on 16 Sept 2026. Namespace anything stored on `this`.
      this.zcPrefix = parts[1];
      this.zcSuffix = parts[3];

      var raw = parts[2];
      this.grouped = raw.indexOf(',') !== -1;
      var dot = raw.indexOf('.');
      this.decimals = dot === -1 ? 0 : raw.length - dot - 1;
      this.target = parseFloat(raw.replace(/,/g, ''));
      if (!isFinite(this.target)) return;

      this.duration = parseInt(this.dataset.duration || '1600', 10);

      this.observer = new IntersectionObserver(this.onIntersect.bind(this), { threshold: 0.4 });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    onIntersect(entries) {
      if (!entries[0].isIntersecting) return;
      this.observer.disconnect();
      this.run();
    }

    format(value) {
      var n = this.decimals ? value.toFixed(this.decimals) : String(Math.round(value));
      if (this.grouped) {
        var bits = n.split('.');
        bits[0] = bits[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        n = bits.join('.');
      }
      return this.zcPrefix + n + this.zcSuffix;
    }

    run() {
      var el = this;
      var start = null;

      // Pin the width at the finished number before the first frame, or the
      // figure grows a digit at a time and shoves the row around under it.
      el.style.minWidth = el.offsetWidth + 'px';

      // A number changing 60 times a second is noise to a screen reader. The
      // real value is back in the DOM the moment this finishes.
      el.setAttribute('aria-hidden', 'true');

      function step(now) {
        if (start === null) start = now;
        var t = Math.min(1, (now - start) / el.duration);
        var eased = 1 - Math.pow(1 - t, 3); // ease-out cubic: quick, then settles
        el.textContent = el.format(el.target * eased);

        if (t < 1) { el.frame = requestAnimationFrame(step); return; }

        // Restore the server-rendered string verbatim rather than a formatted
        // guess at it, so "25,000+" comes back exactly as it was written.
        el.textContent = el.finalText;
        el.removeAttribute('aria-hidden');
        el.style.minWidth = '';
      }

      el.frame = requestAnimationFrame(step);
    }
  }

  if (!customElements.get('zc-count-up')) {
    customElements.define('zc-count-up', ZcCountUp);
  }

  /* Sticky buy bar — only appears once the hero CTA has scrolled away */
  class ZcStickyBar extends HTMLElement {
    connectedCallback() {
      // `position: fixed` is measured against the nearest ancestor that has a
      // transform, filter or perspective — not the viewport. Dawn's
      // scroll-reveal animations put a transform on section wrappers, which
      // traps the bar inside the section and it scrolls away with the page
      // (most obvious on mobile). Re-homing the bar on <body> removes every
      // possible trapping ancestor. Moving it re-fires connectedCallback,
      // and the parent check stops that from looping.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }

      this.threshold = parseInt(this.dataset.threshold || '600', 10);
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      if (!this.onScroll) return;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    onScroll() {
      // Never let the threshold exceed what this screen can actually scroll,
      // or the bar can never appear on a short page / small viewport.
      var scrollable = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      var trigger = Math.min(this.threshold, scrollable * 0.6);
      this.classList.toggle('is-visible', window.scrollY > trigger);
    }
  }

  if (!customElements.get('zc-sticky-bar')) {
    customElements.define('zc-sticky-bar', ZcStickyBar);
  }

  /* Hero product gallery — thumbnails swap the main image */
  class ZcGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-zc-main]');
      this.thumbs = [].slice.call(this.querySelectorAll('[data-zc-thumb]'));
      if (!this.main || !this.thumbs.length) return;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);

      this.stage = this.main.parentElement;
      if (this.stage) {
        this.stage.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.stage.addEventListener('touchend', this.onTouchEnd, { passive: true });
      }

      // Preload the full-size versions so swapping feels instant.
      this.thumbs.forEach(function (btn) {
        var src = btn.dataset.full;
        if (src) { var img = new Image(); img.src = src; }
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.stage) {
        this.stage.removeEventListener('touchstart', this.onTouchStart);
        this.stage.removeEventListener('touchend', this.onTouchEnd);
      }
    }

    onClick(event) {
      if (event.target.closest('[data-zc-prev]')) { this.step(-1); return; }
      if (event.target.closest('[data-zc-next]')) { this.step(1); return; }
      var btn = event.target.closest('[data-zc-thumb]');
      if (btn) this.select(btn);
    }

    onTouchStart(event) {
      this.touchX = event.changedTouches[0].clientX;
      this.touchY = event.changedTouches[0].clientY;
    }

    onTouchEnd(event) {
      if (this.touchX == null) return;
      var dx = event.changedTouches[0].clientX - this.touchX;
      var dy = event.changedTouches[0].clientY - this.touchY;
      this.touchX = null;
      // Horizontal swipe only — don't hijack vertical scrolling.
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      this.step(dx < 0 ? 1 : -1);
    }

    step(delta) {
      var current = this.thumbs.findIndex(function (b) {
        return b.getAttribute('aria-pressed') === 'true';
      });
      if (current === -1) current = 0;
      var next = (current + delta + this.thumbs.length) % this.thumbs.length;
      this.select(this.thumbs[next]);
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      var current = this.thumbs.indexOf(document.activeElement.closest('[data-zc-thumb]'));
      if (current === -1) return;
      event.preventDefault();
      var step = event.key === 'ArrowRight' ? 1 : -1;
      var next = this.thumbs[(current + step + this.thumbs.length) % this.thumbs.length];
      next.focus();
      this.select(next);
    }

    select(btn) {
      var full = btn.dataset.full;
      if (full) {
        this.main.removeAttribute('srcset');
        this.main.src = full;
        var thumbImg = btn.querySelector('img');
        if (thumbImg && thumbImg.alt) this.main.alt = thumbImg.alt;
      }
      this.thumbs.forEach(function (b) {
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });
    }
  }

  if (!customElements.get('zc-gallery')) {
    customElements.define('zc-gallery', ZcGallery);
  }
})();